import React, {useCallback, useEffect, useRef, useState} from "react";
import "./message.css";
import {
    Send as SendIcon,
    PersonOutline as PersonIcon,
    AttachFile as AttachFileIcon,
    Close as CloseIcon,
    InsertDriveFile as FileIcon,
    Download as DownloadIcon,
} from "@mui/icons-material";
import {ApiDetails, KEY} from "../../dummyData";
import {useFeatureFlags} from "../../context/FeatureFlagsContext";
import DisabledFeatureBanner from "../../components/FeatureGate/DisabledFeatureBanner";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import {
    TextField,
    Paper,
    IconButton,
    Typography,
    Avatar,
    Box,
    Tooltip,
    useMediaQuery,
    useTheme,
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
} from "@mui/material";
import CryptoJS from "crypto-js";
import useMessagePages, {MESSAGE_PAGE_SIZE} from "../../utils/useMessagePages";

export default function MessageModern(props) {
    const { isEnabled, getMessage } = useFeatureFlags();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );
    let originalText = details.toString(CryptoJS.enc.Utf8);
    const tenantDetails = new Map(Object.entries(JSON.parse(originalText)));
    const accessToken = tenantDetails.get("access_token");

    const [sendingMessage, setSendingMessage] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [error, setError] = useState(null);
    const [landlordName, setLandlordName] = useState("Manager");
    const [lastSeen, setLastSeen] = useState("Online");
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreviewUrl, setFilePreviewUrl] = useState(null);
    const [viewerItem, setViewerItem] = useState(null);

    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const objectUrlsRef = useRef([]);

    // One page of the conversation: the latest messages, or those before beforeId.
    const fetchPage = useCallback((beforeId) =>
        fetch(ApiDetails + "pegasus/visionary/tenant/app/messages/get", {
            method: "POST",
            headers: { "token": accessToken },
            body: JSON.stringify({
                id: tenantDetails.get("tenant_id"),
                limit: MESSAGE_PAGE_SIZE,
                ...(beforeId ? { before_id: beforeId } : {}),
            }),
        }).then(async (response) => {
            if (response.status === 403) {
                localStorage.clear();
                window.location.href = "/";
                return [];
            }
            if (response.status !== 200) {
                setError("Failed to fetch messages");
                props.snackBar({ text: "Failed fetching messages", type: "error" });
                return [];
            }
            setError(null);
            return response.json();
        }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

    const thread = useMessagePages(fetchPage, tenantDetails.get("tenant_id"));
    const messages = thread.messages;
    const loading = thread.loading;

    useEffect(() => {
        // New messages arrive on a poll; older pages stay loaded.
        const intervalId = setInterval(() => thread.refreshLatest().catch((err) => console.error(err)), 15000);
        return () => {
            clearInterval(intervalId);
            objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (inputRef.current) inputRef.current.focus();
    }, [loading]);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            props.snackBar({ text: "File must be under 10 MB", type: "error" });
            return;
        }

        setSelectedFile(file);
        if (file.type.startsWith("image/")) {
            const url = URL.createObjectURL(file);
            objectUrlsRef.current.push(url);
            setFilePreviewUrl(url);
        } else {
            setFilePreviewUrl(null);
        }
        e.target.value = "";
    };

    const removeSelectedFile = () => {
        setSelectedFile(null);
        setFilePreviewUrl(null);
    };

    const sendMessage = () => {
        const trimmedMessage = messageText.trim();
        if (!trimmedMessage && !selectedFile) {
            props.snackBar({ text: "Cannot send an empty message", type: "error" });
            return;
        }

        setSendingMessage(true);

        const fileToSend = selectedFile;
        const previewUrl = filePreviewUrl;
        const isImage = fileToSend && fileToSend.type.startsWith("image/");

        const optimisticMessage = {
            sender_id: tenantDetails.get("tenant_id"),
            message: trimmedMessage,
            date_created: new Date().toLocaleString(),
            type: fileToSend ? (isImage ? "image" : "file") : "text",
            file_url: previewUrl,
            file_name: fileToSend?.name,
            _optimistic: true,
        };

        thread.appendOptimistic(optimisticMessage);
        setMessageText("");
        setSelectedFile(null);
        setFilePreviewUrl(null);

        let fetchOptions;
        if (fileToSend) {
            const formData = new FormData();
            formData.append("sender_id", tenantDetails.get("tenant_id"));
            formData.append("recipient_id", tenantDetails.get("landlord_id"));
            formData.append("message", trimmedMessage);
            formData.append("file", fileToSend);
            fetchOptions = {
                method: "POST",
                headers: { "token": accessToken },
                body: formData,
            };
        } else {
            fetchOptions = {
                method: "POST",
                headers: { "token": accessToken, "Content-Type": "application/json" },
                body: JSON.stringify({
                    sender_id: tenantDetails.get("tenant_id"),
                    recipient_id: tenantDetails.get("landlord_id"),
                    message: trimmedMessage,
                }),
            };
        }

        fetch(ApiDetails + "pegasus/visionary/tenant/app/messages/send", fetchOptions)
            .then(async (response) => {
                if (response.status === 403) {
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }
                if (response.status === 200) {
                    thread.refreshLatest().catch((err) => console.error(err));
                } else {
                    props.snackBar({ text: "Failed to send message", type: "error" });
                    thread.dropOptimistic();
                }
                setSendingMessage(false);
            })
            .catch((err) => {
                console.error(err);
                props.snackBar({ text: "Something went wrong. Please try again", type: "error" });
                thread.dropOptimistic();
                setSendingMessage(false);
            });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const formatMessageDate = (dateString) => {
        try {
            const msgDate = new Date(dateString);
            const today = new Date();
            if (msgDate.toDateString() === today.toDateString()) {
                return msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
            } else if (msgDate.getFullYear() === today.getFullYear()) {
                return msgDate.toLocaleDateString([], {month: 'short', day: 'numeric'}) +
                    ' at ' + msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
            } else {
                return msgDate.toLocaleDateString([], {year: 'numeric', month: 'short', day: 'numeric'}) +
                    ' at ' + msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
            }
        } catch (e) {
            return dateString;
        }
    };

    const getInitials = (name) => {
        if (!name) return 'PM';
        const nameParts = name.split(' ');
        if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    };

    const openViewer = (message) => setViewerItem(message);
    const closeViewer = () => setViewerItem(null);

    const downloadFile = (url, name) => {
        const a = document.createElement('a');
        a.href = url;
        a.download = name || 'download';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const renderMessageContent = (message) => {
        const isMyMessage = message.sender_id === tenantDetails.get("tenant_id");
        const textColor = isMyMessage ? '#1565C0' : '#424242';

        if (message.type === "image" && message.file_url) {
            return (
                <Box>
                    <Box
                        component="img"
                        src={message.file_url}
                        alt={message.file_name || "image"}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: 260,
                            borderRadius: '12px',
                            display: 'block',
                            cursor: 'pointer',
                            objectFit: 'contain',
                        }}
                        onClick={() => openViewer(message)}
                    />
                    {message.message && (
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 1,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: textColor,
                                fontSize: '15px',
                                lineHeight: 1.5,
                            }}
                        >
                            {message.message}
                        </Typography>
                    )}
                </Box>
            );
        }

        if (message.type === "file" && message.file_url) {
            return (
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            bgcolor: 'rgba(0,0,0,0.04)',
                            borderRadius: '10px',
                            cursor: 'pointer',
                        }}
                        onClick={() => openViewer(message)}
                    >
                        <FileIcon sx={{color: '#6B7280', flexShrink: 0}} />
                        <Typography
                            variant="body2"
                            sx={{
                                color: textColor,
                                fontWeight: 500,
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {message.file_name || "File"}
                        </Typography>
                        <DownloadIcon sx={{color: '#6B7280', flexShrink: 0, fontSize: 18}} />
                    </Box>
                    {message.message && (
                        <Typography
                            variant="body1"
                            sx={{
                                mt: 1,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: textColor,
                                fontSize: '15px',
                                lineHeight: 1.5,
                            }}
                        >
                            {message.message}
                        </Typography>
                    )}
                </Box>
            );
        }

        return (
            <Typography
                variant="body1"
                component="div"
                sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: textColor,
                    fontSize: '15px',
                    lineHeight: 1.5,
                    fontFamily: 'inherit',
                    textAlign: 'left',
                    margin: 0,
                    padding: 0,
                    '& *': { whiteSpace: 'pre-wrap !important' }
                }}
                dangerouslySetInnerHTML={{__html: message.message}}
            />
        );
    };

    const renderMessage = (message, index) => {
        const isMyMessage = message.sender_id === tenantDetails.get("tenant_id");
        const isOptimistic = message._optimistic;

        return (
            <Box
                key={message._optimistic ? `local-${index}` : (message.id ?? index)}
                sx={{
                    display: 'flex',
                    justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                    mb: 3,
                    opacity: isOptimistic ? 0.7 : 1,
                }}
            >
                {(!isMobile && !isMyMessage) && (
                    <Avatar
                        sx={{
                            bgcolor: '#3B82F6',
                            width: 42,
                            height: 42,
                            mr: 2,
                            mt: 1,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        {getInitials(landlordName)}
                    </Avatar>
                )}

                <Box
                    sx={{
                        maxWidth: '75%',
                        bgcolor: isMyMessage ? '#E3F2FD' : 'white',
                        borderRadius: '20px',
                        borderTopRightRadius: isMyMessage ? '6px' : '20px',
                        borderTopLeftRadius: !isMyMessage ? '6px' : '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        p: "10px",
                        position: 'relative',
                        border: isMyMessage ? '1px solid #BBDEFB' : '1px solid #F0F0F0'
                    }}
                >
                    {renderMessageContent(message)}

                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            color: '#9E9E9E',
                            mt: 1.5,
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            fontSize: '11px'
                        }}
                    >
                        {formatMessageDate(message.date_created)}
                        {isOptimistic && <CircularProgress size={10} sx={{ml: 1}}/>}
                    </Typography>
                </Box>

                {(!isMobile && isMyMessage) && (
                    <Avatar
                        sx={{
                            bgcolor: '#4CAF50',
                            width: 42,
                            height: 42,
                            ml: 2,
                            mt: 1,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        {getInitials(tenantDetails.get("first_name") + " " + tenantDetails.get("last_name"))}
                    </Avatar>
                )}
            </Box>
        );
    };

    if (!isEnabled('messages')) {
        return <DisabledFeatureBanner featureName="Messages" message={getMessage('messages')} />;
    }

    const canSend = !sendingMessage && (messageText.trim() !== "" || selectedFile !== null);

    return (
        <Box
            sx={{
                height: 'calc(100vh - 80px)',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f9fafb',
                position: 'relative'
            }}
        >
            <Backdrop sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}} open={loading}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            {/* Chat Header */}
            <Paper
                elevation={1}
                sx={{
                    p: 1,
                    bgcolor: 'white',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 0,
                    zIndex: 1
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Avatar sx={{bgcolor: '#3B82F6', width: 50, height: 50, fontSize: '1.5rem', fontWeight: 600}}>
                        {getInitials(landlordName)}
                    </Avatar>
                    <Box sx={{ml: 3}}>
                        <Typography variant="h5" component="div" sx={{fontWeight: 700, color: '#1F2937', mb: 0.5}}>
                            {landlordName}
                        </Typography>
                        <Typography
                            variant="body2"
                            component="div"
                            sx={{color: '#10B981', display: 'flex', alignItems: 'center', fontWeight: 500}}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    bgcolor: '#10B981', display: 'inline-block', mr: 1
                                }}
                            />
                            {lastSeen}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Messages Area */}
            <Box
                ref={thread.containerRef}
                onScroll={thread.onScroll}
                sx={{
                    px: "10px",
                    flex: 1,
                    overflowY: 'auto',
                    bgcolor: '#F8FAFC',
                    backgroundImage: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                    position: 'relative',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '10px' },
                    '&::-webkit-scrollbar-thumb:hover': { background: '#94A3B8' },
                }}
            >
                {messages.length === 0 && !loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6B7280'
                        }}
                    >
                        <PersonIcon sx={{fontSize: 64, mb: 2, color: '#D1D5DB'}}/>
                        <Typography variant="h5" sx={{mb: 2, fontWeight: 600, color: '#374151'}}>
                            No messages yet
                        </Typography>
                        <Typography variant="body1" sx={{textAlign: 'center', maxWidth: 300}}>
                            Start the conversation by sending a message to your property manager
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {thread.loadingOlder && (
                            <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                                <CircularProgress size={22}/>
                            </Box>
                        )}
                        {messages.map((message, index) => renderMessage(message, index))}
                    </>
                )}
            </Box>

            {/* Message Input Area */}
            <Paper
                elevation={1}
                sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'white',
                    borderRadius: 0,
                    borderTop: '1px solid #E5E7EB',
                }}
            >
                {/* File preview strip */}
                {selectedFile && (
                    <Box sx={{mb: 1, display: 'flex', alignItems: 'center', gap: 1}}>
                        {filePreviewUrl ? (
                            <Box
                                component="img"
                                src={filePreviewUrl}
                                alt="preview"
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '8px',
                                    objectFit: 'cover',
                                    border: '1px solid #E5E7EB',
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.5,
                                    py: 0.75,
                                    bgcolor: '#F3F4F6',
                                    borderRadius: '8px',
                                    border: '1px solid #E5E7EB',
                                }}
                            >
                                <FileIcon sx={{color: '#6B7280', fontSize: 20}} />
                                <Typography variant="caption" sx={{color: '#374151', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                    {selectedFile.name}
                                </Typography>
                            </Box>
                        )}
                        <Tooltip title="Remove file">
                            <IconButton size="small" onClick={removeSelectedFile} sx={{color: '#9CA3AF'}}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    {/* Hidden file input */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                        style={{display: 'none'}}
                        onChange={handleFileSelect}
                    />

                    <Tooltip title="Attach file">
                        <IconButton
                            onClick={() => fileInputRef.current?.click()}
                            sx={{
                                color: selectedFile ? '#3B82F6' : '#9CA3AF',
                                width: 40,
                                height: 40,
                                '&:hover': { color: '#3B82F6', bgcolor: 'rgba(59,130,246,0.08)' },
                            }}
                        >
                            <AttachFileIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Box sx={{flex: 1}}>
                        <TextField
                            inputRef={inputRef}
                            placeholder="Type..."
                            multiline
                            maxRows={3}
                            value={messageText}
                            onChange={(e) => setMessageText(e.target.value)}
                            onKeyDown={handleKeyPress}
                            variant="outlined"
                            fullWidth
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '20px',
                                    backgroundColor: '#F8FAFC',
                                    fontSize: '14px',
                                    minHeight: '40px',
                                    '& fieldset': { borderColor: '#E5E7EB' },
                                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                                    '&.Mui-focused fieldset': { borderColor: '#3B82F6', borderWidth: '1px' },
                                },
                                '& .MuiInputBase-input': { py: 1.5, px: 2 },
                                '& .MuiInputBase-root': { alignItems: 'center' }
                            }}
                        />
                    </Box>

                    <Tooltip title="Send message">
                        <IconButton
                            disabled={!canSend}
                            onClick={sendMessage}
                            sx={{
                                bgcolor: '#3B82F6',
                                color: 'white',
                                width: 44,
                                height: 44,
                                '&:hover': { bgcolor: '#2563EB', transform: 'scale(1.03)' },
                                '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#D1D5DB' },
                                transition: 'all 0.15s ease-in-out'
                            }}
                        >
                            {sendingMessage ? (
                                <CircularProgress size={20} sx={{color: 'white'}}/>
                            ) : (
                                <SendIcon sx={{fontSize: 20}}/>
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            {/* Attachment viewer dialog */}
            <Dialog
                open={!!viewerItem}
                onClose={closeViewer}
                maxWidth="md"
                fullWidth
                PaperProps={{sx: {borderRadius: 3, overflow: 'hidden'}}}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1,
                        borderBottom: '1px solid #E5E7EB',
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                            mr: 2,
                        }}
                    >
                        {viewerItem?.file_name || 'Attachment'}
                    </Typography>
                    <Box sx={{display: 'flex', gap: 1, flexShrink: 0}}>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => downloadFile(viewerItem?.file_url, viewerItem?.file_name)}
                            sx={{bgcolor: '#3B82F6', '&:hover': {bgcolor: '#2563EB'}, textTransform: 'none'}}
                        >
                            Download
                        </Button>
                        <IconButton size="small" onClick={closeViewer}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        bgcolor: viewerItem?.type === 'image' ? '#111827' : '#fff',
                        minHeight: 300,
                    }}
                >
                    {viewerItem?.type === 'image' ? (
                        <Box
                            component="img"
                            src={viewerItem?.file_url}
                            alt={viewerItem?.file_name || 'image'}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '72vh',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    ) : (
                        <Box
                            component="iframe"
                            src={viewerItem?.file_url
                                ? `https://docs.google.com/viewer?url=${encodeURIComponent(viewerItem.file_url)}&embedded=true`
                                : undefined}
                            title={viewerItem?.file_name || 'document'}
                            sx={{
                                width: '100%',
                                height: '72vh',
                                border: 'none',
                                display: 'block',
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
